import isEqual from 'fast-deep-equal';

import type {ConversationOverview} from './types';

/**
 * The frontend's canonical overview state for one conversation: server snapshots
 * reconciled with confirmed local lifecycle transitions. The controller owns
 * this state independently of selection and mounted views, using the overview
 * for history, conversation presentation, and transport/retention decisions.
 *
 * One conversation has at most one chat or callback lifecycle. Once observed,
 * handoff and completion survive stale server responses; metadata can still change.
 */
export type CanonicalOverviewState = {
  id: string;
  /** Null means unknown, before either a snapshot or a confirmed transition. */
  overview: ConversationOverview | null;
};

/** Reserve an identity without assuming the assistant is active or ended. */
export function buildCanonicalOverviewState(
  id: string,
): CanonicalOverviewState {
  return {id, overview: null};
}

// Immutable updates retain the state reference on no-ops so the controller can
// skip repeated notifications and runtime work.
function updateOverview(
  overviewState: CanonicalOverviewState,
  overview: ConversationOverview,
): CanonicalOverviewState {
  return isEqual(overviewState.overview, overview)
    ? overviewState
    : {...overviewState, overview};
}

// A confirmed transition can precede retrieval. Seed just enough overview state
// to remember it; a later snapshot supplies metadata. Unobserved state stays null.
function overviewForTransition(
  overviewState: CanonicalOverviewState,
): ConversationOverview {
  return (
    overviewState.overview ?? {
      id: overviewState.id,
      last_updated_at: 0,
      is_active: false,
    }
  );
}

/**
 * Merge a matching server snapshot without losing established support or undoing
 * completion learned locally or from the server. Omission is not evidence that
 * support ended. last_updated_at is message recency, not a lifecycle revision.
 */
export function reconcileOverview(
  overviewState: CanonicalOverviewState,
  incoming?: ConversationOverview,
): CanonicalOverviewState {
  if (!incoming || incoming.id !== overviewState.id) {
    return overviewState;
  }
  const previous = overviewState.overview;
  let chat = previous?.live_chat;
  let callback = previous?.request_a_call;
  // Conflicting transport identities are invalid, not replacement sessions.
  if (
    !callback &&
    (!chat || chat.external_id === incoming.live_chat?.external_id)
  ) {
    chat = chat?.status === 'ended' ? chat : incoming.live_chat ?? chat;
  }
  if (!chat) {
    callback =
      callback?.status === 'ended'
        ? callback
        : incoming.request_a_call ?? callback;
  }
  const overview = {...incoming};
  if (chat) {
    overview.live_chat = chat;
    delete overview.request_a_call;
  }
  if (callback) {
    overview.request_a_call = callback;
    delete overview.live_chat;
  }
  // is_active describes the assistant only: handoff ends it while support may
  // remain active. Neither handoff nor assistant completion can be reversed.
  overview.is_active = !!(
    incoming.is_active &&
    previous?.is_active !== false &&
    !chat &&
    !callback
  );
  // Only lifecycle fields survive omission; metadata follows the latest response.
  return updateOverview(
    overviewState,
    isEqual(incoming, overview) ? incoming : overview,
  );
}

/** Record a confirmed chat handoff; repeated starts cannot reopen ended support. */
export function recordChatStarted(
  overviewState: CanonicalOverviewState,
  externalId: string,
) {
  if (
    !externalId ||
    overviewState.overview?.live_chat ||
    overviewState.overview?.request_a_call
  ) {
    return overviewState;
  }
  return updateOverview(overviewState, {
    ...overviewForTransition(overviewState),
    is_active: false,
    live_chat: {external_id: externalId, status: 'active'},
  });
}

/** Record a confirmed callback handoff, ending assistant activity while it waits. */
export function recordCallbackQueued(overviewState: CanonicalOverviewState) {
  if (
    overviewState.overview?.request_a_call ||
    overviewState.overview?.live_chat
  ) {
    return overviewState;
  }
  return updateOverview(overviewState, {
    ...overviewForTransition(overviewState),
    is_active: false,
    request_a_call: {status: 'queued'},
  });
}

/**
 * Record confirmed assistant completion (including successful email submission).
 * This does not end an established chat or callback.
 */
export function recordAssistantCompletion(
  overviewState: CanonicalOverviewState,
) {
  return updateOverview(overviewState, {
    ...overviewForTransition(overviewState),
    is_active: false,
  });
}

export type CompletionArgs =
  | [channel: 'live_chat', externalId: string]
  | [channel: 'request_a_call'];

/**
 * Record confirmed support completion before a webhook-backed overview catches up.
 * Call after success or a terminal event, never an optimistic/failed close. Chat
 * identity checks prevent unrelated transport events from ending this conversation.
 */
export function recordCompletion(
  overviewState: CanonicalOverviewState,
  ...args: CompletionArgs
) {
  const overview = overviewForTransition(overviewState);
  if (args[0] === 'live_chat') {
    const externalId = args[1];
    if (
      !externalId ||
      overview.request_a_call ||
      (overview.live_chat && overview.live_chat.external_id !== externalId)
    ) {
      return overviewState;
    }
    return updateOverview(overviewState, {
      ...overview,
      is_active: false,
      live_chat: {external_id: externalId, status: 'ended'},
    });
  }
  if (overview.live_chat) {
    return overviewState;
  }
  return updateOverview(overviewState, {
    ...overview,
    is_active: false,
    request_a_call: {status: 'ended'},
  });
}
